import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Card } from '@orion/ui';
import { NOTIFICATION_EVENT_KEYS, type NotificationSettings } from '@orion/contracts';
import { PageHeader } from '../../app/shell.js';
import { useMembershipsQuery } from './users/api.js';
import { useRolesQuery } from './roles/api.js';
import { useEntraConfigQuery } from './sso/api.js';
import { useSettingsQuery } from './settings/api.js';
import { useCustomFieldsQuery } from './custom-fields/api.js';
import { useApiKeysQuery } from './api-keys/api.js';
import { useWebhookSubscriptionsQuery } from './webhooks/api.js';
import { useAuditLogsQuery } from './audit/api.js';
import { useLeadStatusOptionsQuery, useLeadSourceOptionsQuery } from '../leads/api.js';
import { usePipelinesQuery } from '../deals/api.js';
import { useImportJobsQuery } from '../imports/api.js';

interface SecuritySettings {
  mfaRequiredFor: Array<'admins' | 'managers' | 'everyone'>;
}
interface EmailSettings {
  provider: string;
}

const EMAIL_PROVIDER_LABEL: Record<string, string> = { brevo: 'Brevo', smtp: 'SMTP', msgraph: 'Microsoft 365', gmail: 'Gmail', postmark: 'Postmark' };
const CUSTOM_FIELD_ENTITIES = ['lead', 'contact', 'company', 'opportunity'] as const;

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const hr = Math.round(ms / 3_600_000);
  if (hr < 1) return 'less than an hour ago';
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

/** docs section 5 "Administrator Control" — the admin landing page. Every
 * card's stat line reads a real admin endpoint (design/orion-crm-design/
 * rendered-html/AdminHome.html shows the same shape: a count or a
 * connection status, not decoration).
 *
 * The design also shows a "Setup progress" checklist (5/7 steps, a
 * "Recommended next" callout) — no onboarding-checklist feature exists
 * anywhere in this codebase (which of the 7 steps are "done" isn't tracked
 * for any of them), so it's omitted rather than showing a fabricated
 * percentage. Search settings filters the cards below by title (real,
 * client-side — there's no settings-search index to query). */
export function AdminOverviewPage() {
  const [q, setQ] = useState('');
  const { data: memberships } = useMembershipsQuery();
  const { data: roles } = useRolesQuery();
  const { data: entra } = useEntraConfigQuery();
  const { data: security } = useSettingsQuery<SecuritySettings>('security');
  const { data: email } = useSettingsQuery<EmailSettings>('email');
  const { data: notifications } = useSettingsQuery<NotificationSettings>('notifications');
  const { data: leadStatuses } = useLeadStatusOptionsQuery();
  const { data: leadSources } = useLeadSourceOptionsQuery();
  const { data: pipelines } = usePipelinesQuery();
  const { data: apiKeys } = useApiKeysQuery();
  const { data: webhooks } = useWebhookSubscriptionsQuery();
  const { data: auditLogs } = useAuditLogsQuery({ page: 1 });
  const { data: importJobs } = useImportJobsQuery();
  const { data: leadFields } = useCustomFieldsQuery('lead');
  const { data: contactFields } = useCustomFieldsQuery('contact');
  const { data: companyFields } = useCustomFieldsQuery('company');
  const { data: dealFields } = useCustomFieldsQuery('opportunity');

  const customFieldTotal = (leadFields?.length ?? 0) + (contactFields?.length ?? 0) + (companyFields?.length ?? 0) + (dealFields?.length ?? 0);
  const activeCount = memberships?.data.filter((m) => m.status === 'active').length ?? 0;
  const invitedCount = memberships?.data.filter((m) => m.status === 'invited').length ?? 0;
  const customRoleCount = roles?.filter((r) => !r.isSystem).length ?? 0;
  const totalStages = pipelines?.reduce((sum, p) => sum + p.stages.length, 0) ?? 0;
  const failingWebhooks = webhooks?.filter((w) => w.disabledAt !== null).length ?? 0;
  const notificationsEnabledCount = notifications
    ? NOTIFICATION_EVENT_KEYS.reduce((n, key) => n + (notifications[key].inApp ? 1 : 0) + (notifications[key].email ? 1 : 0), 0)
    : 0;
  const lastImport = importJobs && importJobs.length > 0 ? importJobs[0] : undefined;

  const CARDS = useMemo(
    (): Array<{ to: string; title: string; description: string; stat: ReactNode; icon: string; iconRect?: boolean; tone?: 'success' | 'warning' }> => [
      { to: '/admin/users', title: 'Users', description: 'Invite people, assign roles and teams.', icon: 'M9 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7M16 4.1a4 4 0 0 1 0 7.8M22 21c0-3-1.8-5.6-4.5-6.6', stat: memberships ? `${activeCount} active · ${invitedCount} invited` : '—' },
      { to: '/admin/roles', title: 'Roles & permissions', description: 'Control what each role can see and do.', icon: 'M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z M9 12l2 2 4-4', stat: roles ? `${roles.length} roles · ${customRoleCount} custom` : '—' },
      { to: '/admin/sso', title: 'Authentication & SSO', description: 'Sign-in methods and single sign-on.', icon: 'M8 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8M11 12l9-9M17 6l3 3', stat: entra ? (entra.enabled ? 'Microsoft Entra: Connected' : 'Microsoft Entra: Not connected') : '—', tone: entra?.enabled ? 'success' : undefined },
      { to: '/admin/security', title: 'Security', description: 'Passwords, MFA, sessions and lockout.', icon: 'M4 11h16v10H4z M8 11V7a4 4 0 0 1 8 0v4', iconRect: true, stat: security ? `MFA required: ${security.mfaRequiredFor.length === 0 ? 'no one' : security.mfaRequiredFor.join(', ')}` : '—' },
      { to: '/admin/leads', title: 'Leads', description: 'Statuses, sources, scoring and routing.', icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18 5 5 0 1 0 0 10 5 5 0 0 0 0-10', stat: leadStatuses && leadSources ? `${leadStatuses.length} statuses · ${leadSources.length} sources` : '—' },
      { to: '/admin/pipelines', title: 'Pipelines', description: 'Opportunity stages, probabilities and rules.', icon: 'M4 4h5v16H4z M10 4h5v11h-5z M17 4h4v6h-4z', stat: pipelines ? `${pipelines.length} pipeline${pipelines.length === 1 ? '' : 's'} · ${totalStages} stages` : '—' },
      { to: '/admin/custom-fields', title: 'Custom fields', description: 'Extra fields on leads, opportunities and more.', icon: 'M3 4h18v16H3z M7 9h10M7 13h6M7 17h3', iconRect: true, stat: `${customFieldTotal} fields across ${CUSTOM_FIELD_ENTITIES.length} objects` },
      { to: '/admin/email', title: 'Email', description: 'Sending provider and from-address.', icon: 'M3 5h18v14H3z M3 7l9 6 9-6', iconRect: true, stat: email ? `${EMAIL_PROVIDER_LABEL[email.provider] ?? email.provider} connected` : '—' },
      { to: '/admin/notifications', title: 'Notifications', description: 'Which events notify, in-app and by email.', icon: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.9 1.9 0 0 0 3.4 0', stat: notifications ? `${notificationsEnabledCount} of ${NOTIFICATION_EVENT_KEYS.length * 2} alerts on` : '—' },
      { to: '/leads/import', title: 'Import & export', description: 'CSV imports, field mapping and exports.', icon: 'M7 4v14M3 14l4 4 4-4 M17 20V6M13 10l4-4 4 4', stat: lastImport ? `Last import ${timeAgo(lastImport.createdAt)}` : 'No imports yet' },
      { to: '/admin/webhooks', title: 'API keys & webhooks', description: 'Keys for integrations and event delivery.', icon: 'M8 8l-5 4 5 4M16 8l5 4-5 4M14 5l-4 14', stat: apiKeys && webhooks ? `${apiKeys.length + webhooks.length} endpoints${failingWebhooks > 0 ? ` · ${failingWebhooks} failing` : ''}` : '—', tone: failingWebhooks > 0 ? 'warning' : undefined },
      { to: '/admin/audit-log', title: 'Audit log', description: 'Every admin and data change, searchable.', icon: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z M14 3v6h6M8 13h8M8 17h5', stat: auditLogs ? `${auditLogs.meta?.total ?? auditLogs.data.length} events logged` : '—' },
    ],
    [memberships, roles, entra, security, leadStatuses, leadSources, pipelines, customFieldTotal, email, notifications, lastImport, apiKeys, webhooks, auditLogs, activeCount, invitedCount, customRoleCount, totalStages, notificationsEnabledCount, failingWebhooks],
  );

  const filtered = CARDS.filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()) || c.description.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHeader title="Administration" />
      <main className="flex flex-1 flex-col gap-5 overflow-y-auto px-8 py-7">
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">Administration</h1>
            <p className="text-sm leading-5 text-text-muted">Configure how OES Workspace works for everyone — access, CRM structure, communication and data.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex h-9 w-58 items-center gap-2 rounded-control border border-border-strong bg-surface px-3">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" />
              </svg>
              <span className="sr-only">Search settings</span>
              <input value={q} onChange={(e) => setQ(e.target.value)} type="search" placeholder="Search settings…" className="w-full bg-transparent text-sm text-ink placeholder:text-text-muted focus:outline-none" />
            </label>
            <Link to="/admin/users" className="inline-flex h-9 items-center gap-1.5 rounded-control bg-primary px-3.5 text-sm font-semibold text-white hover:bg-primary-hover">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Invite users
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {filtered.map((c) => (
            <Link key={c.to} to={c.to}>
              <Card className="flex h-full flex-col gap-2 p-4 hover:shadow-popover">
                <div className="flex items-center gap-2.5">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-input bg-ground">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      {c.iconRect && <rect x="3" y="4" width="18" height="16" rx="2" />}
                      <path d={c.icon} />
                    </svg>
                  </span>
                  <span className="flex-1 text-[15px] font-semibold text-ink">{c.title}</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9A9FA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                </div>
                <span className="text-[13px] leading-4.5 text-text-muted">{c.description}</span>
                <span
                  className="flex items-center gap-1.5 border-t border-divider pt-2 text-xs font-semibold"
                  style={{ color: c.tone === 'success' ? 'var(--color-success)' : c.tone === 'warning' ? 'var(--color-amber-text)' : 'var(--color-text-secondary)' }}
                >
                  <span className="size-1.5 rounded-full" style={{ background: c.tone === 'success' ? 'var(--color-success)' : c.tone === 'warning' ? 'var(--color-amber-deep)' : 'var(--color-text-faint)' }} />
                  {c.stat}
                </span>
              </Card>
            </Link>
          ))}
        </div>
      </main>
    </>
  );
}
